import { css } from '@emotion/react'
import { Card, Tooltip } from 'plume-ui'
import { useState } from 'react'
import { equal } from '@/lib/collection'
import { formatNumberWithUnit } from '@/lib/formatter'
import { formatLastFundingType } from '@/lib/roundtype'
import { OverseasCompanyData } from '../../../../../types/api'
import { CardInnerTitle } from '../../../../atoms/CardInnerTitle'
import {
  Table,
  TableCell,
  TableHead,
  TableHeading,
  TableRow,
} from '../../../../atoms/Table'
import { OpenCloseToggle } from '../../../../common/OpenCloseToggle'
import { OverseasCompanyFundingRoundInvestments } from './OverseasComapnyFundingRoundInvestments'
import { FormattedDate } from '../../../../atoms/FormattedDate'

type Props = {
  overseaFundingRounds: OverseasCompanyData['overseaFundingRounds']
}

export const OverseasCompanyFundingRounds: React.FC<Props> = ({
  overseaFundingRounds,
}) => {
  const [openingIndexes, setOpeningIndexes] = useState<number[]>([])

  const roundWithDetailsIndexes: number[] = overseaFundingRounds.reduce(
    (acc, curr, idx) => (curr.investments.length > 0 ? acc.concat(idx) : acc),
    Array<number>(0)
  )

  const toggleRoundDetails = (index: number) => {
    if (isRoundOpen(index)) {
      setOpeningIndexes((pre) => pre.filter((s) => s !== index))
    } else {
      setOpeningIndexes((pre) => [...pre, index])
    }
  }

  const isRoundOpen = (index: number) => openingIndexes.includes(index)

  const toggleAllRoundDetails = () => {
    if (isAllRoundsOpen) {
      setOpeningIndexes([])
    } else {
      setOpeningIndexes(roundWithDetailsIndexes)
    }
  }

  const hasRoundWithDetails = roundWithDetailsIndexes.length !== 0

  const isAllRoundsOpen =
    hasRoundWithDetails && equal(roundWithDetailsIndexes, openingIndexes)

  const buttonLabel = !hasRoundWithDetails
    ? ''
    : isAllRoundsOpen
      ? 'すべて閉じる'
      : 'すべて開く'

  return (
    <Card>
      <CardInnerTitle title="ラウンド情報" />
      <Table className="round-table">
        <TableHead
          css={css`
            height: 48px;
          `}
        >
          <tr
            css={css`
              th {
                padding: 16px;
              }
            `}
          >
            <TableHeading
              width="40px"
              align="center"
              justify="center"
              className="toggle"
            >
              <Tooltip label={buttonLabel}>
                <OpenCloseToggle
                  status={
                    isAllRoundsOpen
                      ? 'open'
                      : hasRoundWithDetails
                        ? 'closed'
                        : 'disabled'
                  }
                  onClick={toggleAllRoundDetails}
                  isLarge
                />
              </Tooltip>
            </TableHeading>
            <TableHeading width="104px">発表日</TableHeading>
            <TableHeading
              width="504px"
              help="定義を見る"
              helpProps={{
                linkTo: 'https://help.initial.inc/ja/articles/5489432',
              }}
            >
              最新ラウンドタイプ
            </TableHeading>
            <TableHeading width="232px" justify="end">
              調達金額
            </TableHeading>
            <TableHeading width="232px" justify="end">
              調達後評価額
            </TableHeading>
          </tr>
        </TableHead>
        <tbody
          css={css`
            tr:last-child td {
              border-bottom: none;
            }
          `}
        >
          {overseaFundingRounds.map((fr, i) => {
            return (
              <>
                <TableRow
                  key={fr.fundingRoundId}
                  css={css`
                    td {
                      padding: 16px;
                    }
                  `}
                  className="round-table-row"
                >
                  <TableCell className="toggle" centerized={true}>
                    {fr.investments.length !== 0 && (
                      <OpenCloseToggle
                        status={isRoundOpen(i) ? 'open' : 'closed'}
                        onClick={() => toggleRoundDetails(i)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {fr.valuationDate && (
                      <FormattedDate>{fr.valuationDate}</FormattedDate>
                    )}
                  </TableCell>
                  <TableCell>
                    {fr.roundType && formatLastFundingType(fr.roundType)}
                  </TableCell>
                  <TableCell align="right">
                    {fr.raisedAmountUsd &&
                      `USD ${formatNumberWithUnit(fr.raisedAmountUsd)}`}
                  </TableCell>
                  <TableCell align="right">
                    {fr.valuationUsd &&
                      `USD ${formatNumberWithUnit(fr.valuationUsd)}`}
                  </TableCell>
                </TableRow>
                {isRoundOpen(i) && (
                  <tr>
                    <td colSpan={5}>
                      <OverseasCompanyFundingRoundInvestments
                        investments={fr.investments}
                      />
                    </td>
                  </tr>
                )}
              </>
            )
          })}
        </tbody>
      </Table>
    </Card>
  )
}
